import { useCTAVisualSource } from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { defaultCTAVisualList } from "@jugl-web/domain-resources/chat-bot/utils/defaultCTAVisuals/defaultCTAVisuals";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import {
  Dialog,
  InteractiveContainer,
  PlainButton,
} from "@jugl-web/ui-components/cross-platform";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import {
  cx,
  isFileTypeAccepted,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { getVideoDuration } from "@jugl-web/utils/utils/files";
import { FC, useCallback, useEffect, useState } from "react";
import { CustomCTAVisualPreview } from "../CustomCTAVisualPreview";
import { DefaultCTAVisualPreview } from "../DefaultCTAVisualPreview";
import { EditableMediaContainer } from "../EditableMediaContainer";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as GearsIcon } from "./assets/gears.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";

const CTA_VISUAL_ACCEPT_TYPES =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm";

const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1 MB
const MAX_CLIP_BYTES = 3 * 1024 * 1024; // 3 MB
const MAX_CLIP_DURATION_SEC = 15.999;

const CUSTOM_MEDIA_TILE_CLASS_NAME =
  "relative flex h-[120px] w-[180px] items-center justify-center overflow-hidden rounded-xl border-4 border-solid border-transparent bg-[#FAFAFA] transition-all hover:border-dark-100";

interface SelectCTAVisualDialogProps {
  isOpen: boolean;
  selectedVisual: string | File | null;
  onVisualChange: (visual: string | File) => void;
  onClose: () => void;
}

const isDefaultCTAVisual = (visual: string | File | null): boolean =>
  typeof visual === "string" && visual.startsWith("default");

export const SelectCTAVisualDialog: FC<SelectCTAVisualDialogProps> = ({
  isOpen,
  selectedVisual,
  onVisualChange,
  onClose,
}) => {
  const [internalVisual, setInternalVisual] = useState<string | File | null>(
    selectedVisual
  );

  const [previousCustomVisual, setPreviousCustomVisual] = useState<
    string | File | null
  >(null);

  const customVisualSrc = useCTAVisualSource(
    internalVisual !== null && !isDefaultCTAVisual(internalVisual)
      ? internalVisual
      : null
  );

  const previousCustomVisualSrc = useCTAVisualSource(previousCustomVisual);

  const hasPreviousCustomVisual =
    previousCustomVisualSrc !== null && customVisualSrc === null;

  const { selectFile } = useFileSelect();

  const { t } = useTranslations();
  const { toast } = useToast();

  const handleFileChange = useCallback(async () => {
    const selectedFiles = await selectFile({
      acceptTypes: CTA_VISUAL_ACCEPT_TYPES,
      maxFileSize: MAX_CLIP_BYTES,
      multiple: false,
    });

    const file = selectedFiles?.[0];

    if (!file) {
      return;
    }

    if (!isFileTypeAccepted(file, CTA_VISUAL_ACCEPT_TYPES)) {
      toast(
        t({
          id: "cx-ai-agents-page.incorrect-file-format",
          defaultMessage: "Incorrect file format ❌",
        }),
        { variant: "error" }
      );
      return;
    }

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast(
        t({
          id: "cx-ai-agents-page.incorrect-file-format",
          defaultMessage: "Incorrect file format ❌",
        }),
        { variant: "error" }
      );
      return;
    }

    if (isImage && file.size > MAX_IMAGE_BYTES) {
      toast(
        t(
          {
            id: "cta-visual-select.image-too-large",
            defaultMessage: "Image must be up to {maxMb} MB",
          },
          { maxMb: 1 }
        ),
        { variant: "error" }
      );
      return;
    }

    if (isVideo) {
      if (file.size > MAX_CLIP_BYTES) {
        toast(
          t(
            {
              id: "cta-visual-select.clip-too-large",
              defaultMessage: "Clip must be up to {maxMb} MB",
            },
            { maxMb: 3 }
          ),
          { variant: "error" }
        );
        return;
      }

      let videoDuration = 0;

      try {
        videoDuration = await getVideoDuration(file);
      } catch {
        // Do nothing, continue with 0 duration
      }

      if (videoDuration > MAX_CLIP_DURATION_SEC) {
        toast(
          t(
            {
              id: "cta-visual-select.clip-too-long",
              defaultMessage: "Clip must be up to {maxSec} seconds",
            },
            { maxSec: Math.floor(MAX_CLIP_DURATION_SEC) }
          ),
          { variant: "error" }
        );
        return;
      }
    }

    setInternalVisual(file);
  }, [selectFile, t, toast]);

  const handleDefaultSelect = useCallback(
    (id: string) => {
      if (internalVisual && !isDefaultCTAVisual(internalVisual)) {
        setPreviousCustomVisual(internalVisual);
      }
      setInternalVisual(id);
    },
    [internalVisual]
  );

  // Reset internal state when external prop changes
  useEffect(() => {
    if (isOpen) {
      setInternalVisual(selectedVisual);
    }
  }, [selectedVisual, isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="font-secondary flex w-[780px] flex-col"
    >
      <div className="border-dark-100 flex h-[60px] w-full shrink-0 items-center justify-between border-0 border-b border-solid px-6">
        <div className="flex items-center gap-2 font-semibold text-[#383838]">
          <GearsIcon />
          {t({
            id: "cta-visual-select.dialog-title",
            defaultMessage: "CTA Visuals",
          })}
        </div>
        <PlainButton className="hover:bg-grey-100 cursor-pointer rounded-lg">
          <CloseIcon onClick={onClose} />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar overflow-y-auto">
        {/* Default CTA visual section */}
        <div className="mx-auto grid w-full max-w-max grid-cols-3 gap-y-5 gap-x-[35px] pt-12">
          {defaultCTAVisualList.map((visual) => (
            <InteractiveContainer
              key={visual.id}
              onClick={() => handleDefaultSelect(visual.id)}
              className={cx(
                "relative h-[120px] w-[180px] overflow-hidden rounded-xl border-4 border-solid border-transparent bg-[#FAFAFA] transition-all",
                typeof internalVisual === "string" &&
                  internalVisual === visual.id
                  ? "border-dark border-4 border-solid"
                  : "hover:border-dark-100"
              )}
            >
              <DefaultCTAVisualPreview src={visual.src} />
            </InteractiveContainer>
          ))}
          {/* Custom CTA visual section */}
          <div className="col-span-3 mt-[7px] grid grid-cols-3 gap-x-[35px]">
            {customVisualSrc || previousCustomVisualSrc ? (
              <EditableMediaContainer
                onSelectNew={handleFileChange}
                onRemove={() => {
                  setPreviousCustomVisual(null);
                  if (customVisualSrc !== null) {
                    setInternalVisual(null);
                  }
                }}
              >
                <InteractiveContainer
                  onClick={
                    hasPreviousCustomVisual
                      ? () => setInternalVisual(previousCustomVisual)
                      : undefined
                  }
                  isDisabled={!!customVisualSrc}
                  className={cx(
                    CUSTOM_MEDIA_TILE_CLASS_NAME,
                    customVisualSrc &&
                      "border-dark hover:border-dark border-4 border-solid"
                  )}
                >
                  <div className="relative h-full w-full">
                    <CustomCTAVisualPreview
                      src={
                        (customVisualSrc || previousCustomVisualSrc) as string
                      }
                      fallback={<PlusIcon />}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </InteractiveContainer>
              </EditableMediaContainer>
            ) : (
              <InteractiveContainer
                onClick={handleFileChange}
                className={CUSTOM_MEDIA_TILE_CLASS_NAME}
              >
                <div className="text-grey-400">
                  <PlusIcon />
                </div>
              </InteractiveContainer>
            )}
            <div className="text-dark-700 nowrap  col-span-2 -ml-1 flex w-full flex-col items-start justify-center gap-1 text-left text-sm">
              <span>
                {t(
                  {
                    id: "cta-visual-select.upload-hint-clip",
                    defaultMessage:
                      "<b>Clip:</b> MP4, GIF, or WEBM (max 3 MB, up to 15 sec)",
                  },
                  {
                    b: (chunks: (string | JSX.Element)[]) => (
                      <span className="font-semibold">{chunks}</span>
                    ),
                  }
                )}
              </span>
              <span>
                {t(
                  {
                    id: "cta-visual-select.upload-hint-image",
                    defaultMessage:
                      "<b>Image:</b> JPG, PNG, or WEBP (max 1 MB)",
                  },
                  {
                    b: (chunks: (string | JSX.Element)[]) => (
                      <span className="font-semibold">{chunks}</span>
                    ),
                  }
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="border-dark-100 mt-12 flex h-[72px] w-full shrink-0 items-center justify-end border border-l-0 border-r-0 border-b-0 border-solid px-6">
        <Button
          onClick={() => {
            if (internalVisual !== null) {
              onVisualChange(internalVisual);
              onClose();
            }
          }}
          iconEnd={<ArrowRightIcon />}
          isDisabled={internalVisual === null}
          className="w-full max-w-[300px]"
        >
          {t({
            id: "common.save",
            defaultMessage: "Save",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
